import { useQueryClient } from '@tanstack/react-query'
import { Loader2Icon, UploadIcon } from 'lucide-react'
import React, { type ChangeEvent, useEffect, useRef, useState } from 'react'

import { Banner, FileUpload, TextLink, useBreakpoint } from 'slab'
import { CENSUS_TEMPLATE_PATH } from 'utils'
import { getGetQuotingProspectsProspectIdCensusQueryKey, useGetQuotingProspectsProspectIdCensusId, usePostQuotingProspectsProspectIdCensus } from 'utils/web_api'

import CensusUploadIcon from './CensusUploadIcon'

type Props = {
  prospectId: string
  onCensusProcessed: (censusId: string) => Promise<void>
}

function InstructionListItem ({ children, number }: { children: React.ReactNode, number: number }) {
  return (
    <li className='flex items-start gap-2'>
      <div className='h-5 flex items-center'>
        <div className='size-4 shrink-0 flex items-center justify-center rounded-full border border-gray-500 text-gray-500 caption font-semibold'>
          {number}
        </div>
      </div>
      <div>{children}</div>
    </li>
  )
}

function CensusFileUploadContent ({
  dropping,
  isProcessing,
}: {
  dropping: boolean;
  isProcessing: boolean;
}) {
  function renderMessage () {
    if (dropping) {
      return 'Drop to upload'
    }

    if (isProcessing) {
      return 'Processing census...'
    }

    return 'Upload a new census'
  }

  return (
    <div className='flex flex-col items-center justify-center gap-10 p-10 h-full'>
      <CensusUploadIcon />
      <div className='flex flex-row items-center justify-center gap-2'>
        {isProcessing
          ? (
            <Loader2Icon size={16} className='text-gray-500 animate-spin' />
            )
          : (
            <UploadIcon size={16} className='text-gray-500' />
            )}
        <span className='text-gray-500 body'>{renderMessage()}</span>
      </div>
    </div>
  )
}

export function CensusUpload ({ prospectId, onCensusProcessed }: Props) {
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [error, setError] = useState<string>()
  const [isProcessing, setIsProcessing] = useState(false)
  const { breakpointIsAtLeast } = useBreakpoint()

  const censusId = useRef<string>()
  const censusProcessed = useRef(false)
  const formDataRef = useRef<FormData>()

  const queryClient = useQueryClient()

  const createCensusMutation = usePostQuotingProspectsProspectIdCensus({
    mutation: {
      onSuccess: (census) => {
        queryClient.invalidateQueries({ queryKey: getGetQuotingProspectsProspectIdCensusQueryKey() })
        censusId.current = census.id
        censusProcessed.current = false
      },
      onError: (error: any) => {
        setError(error?.message || 'Failed to create census')
        setIsProcessing(false)
      },
    },
    request: {
      get body () {
        return formDataRef.current
      }
    }
  }, queryClient)
  const { data: census } = useGetQuotingProspectsProspectIdCensusId(
    prospectId,
    censusId.current || '',
    {
      query: {
        enabled: !!censusId.current,
        retry: false,
        refetchInterval: (query) => {
          if (['processed', 'failed'].includes(query.state.data?.status ?? '')) {
            return false
          }
          return 1000
        },
      }
    }
  )

  useEffect(() => {
    if (census?.status === 'processed' && !censusProcessed.current) {
      censusProcessed.current = true
      onCensusProcessedInternal(census.id)
    } else if (census?.status === 'failed') {
      setIsProcessing(false)
      setError(census.parsing_errors?.join(', ')?.replaceAll(':', '') ?? 'Unknown error')
    }
  }, [census, onCensusProcessed])

  const onCensusProcessedInternal = async (censusId: string) => {
    try {
      await onCensusProcessed(censusId)
    } finally {
      setIsProcessing(false)
    }
  }

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      onFileSelect(file)
    }
  }

  const onFileSelect = (file: File) => {
    if (file.type !== 'text/csv') {
      setError(`Please upload your census as a csv file, provided file was "${file.type}"`)
      return
    }

    const formData = new FormData()
    formData.append('file', file)
    formData.append('file_name', file.name)
    formData.append('format', 'generic')

    setIsProcessing(true)

    formDataRef.current = formData
    createCensusMutation.mutate({ prospectId })
  }

  return (
    <div className='flex flex-col gap-4'>
      <div className='grid xs:grid-cols-[1fr_2fr] gap-4'>
        <div className='bg-white border border-gray-200 rounded-lg p-6'>
          <div className='body-bold mb-4'>
            Follow the steps below to upload your census file:
          </div>
          <ol className='space-y-2'>
            <InstructionListItem number={1}>
              <TextLink color='purple' href={CENSUS_TEMPLATE_PATH} download>
                Download this template
              </TextLink>
            </InstructionListItem>
            <InstructionListItem number={2}>
              Add all employees to the template
            </InstructionListItem>
            <InstructionListItem number={3}>
              Save your file as a .csv
            </InstructionListItem>
            <InstructionListItem number={4}>
              Upload your csv {breakpointIsAtLeast('xs') ? 'on the right' : 'below'}
            </InstructionListItem>
          </ol>
        </div>
        <FileUpload
          accept='text/csv'
          disabled={isProcessing}
          height='full'
          onChange={handleFileChange}
          ref={fileInputRef}
        >
          {({ dropping }) => (
            <CensusFileUploadContent
              dropping={dropping}
              isProcessing={isProcessing}
            />
          )}
        </FileUpload>
      </div>
      {error && (
        <Banner
          title='Failed to process census'
          type='critical'
          onDismiss={() => {
            setError(undefined)
          }}
        >
          {error}
        </Banner>
      )}
    </div>
  )
}
